import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Phone, ShoppingBag, User } from 'lucide-react';
import Logo1 from './assets/download.svg';
import burger_icon from './assets/icon/burger_left.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { getState } from './Firebase/auth';
import MobileNavBar from './MobileNavbar';
import Patti_home from './Homepage/Home/patti';
import SidebarNav from './navslider';
import { motion, useScroll, useTransform, cubicBezier, useMotionValue, useSpring } from 'framer-motion';

export default function NavBar({ flag = 1, style = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);
  const logoRef = useRef(null);

  const { scrollY } = useScroll();
  const smoothScrollY = useSpring(scrollY, { damping: 20, stiffness: 90 });
  const footerLogoOpacity = useMotionValue(1);

  useEffect(() => {
    const handleFooterLogoVisibility = (e) => {
      footerLogoOpacity.set(1 - e.detail.ratio);
    };
    window.addEventListener('footer-logo-visibility', handleFooterLogoVisibility);
    return () => {
      window.removeEventListener('footer-logo-visibility', handleFooterLogoVisibility);
    };
  }, []);

  const isHome = location.pathname === '/';
  const [homeLogoStartScale, setHomeLogoStartScale] = useState(() => {
    const baseLogoWidth = 240;
    const horizontalPadding = 32;
    const availableWidth = Math.max(0, window.innerWidth - horizontalPadding * 2);
    return Math.min(6.4, Math.max(1, availableWidth / baseLogoWidth));
  });

  const logoScale = useTransform(smoothScrollY, [0, 300], [homeLogoStartScale, 1]);
  const logoY = useTransform(smoothScrollY, [0, 300], ['8vh', '0vh']);

  useEffect(() => {
    if (!isHome || isTablet) return;
    const transitionDistance = 300;
    const handleWheel = (e) => {
      const currentScroll = window.scrollY;
      if (currentScroll < transitionDistance - 5) {
        e.preventDefault();
        if (e.deltaY > 0) {
          window.scrollTo({ top: transitionDistance, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else if (currentScroll >= transitionDistance - 5 && currentScroll <= transitionDistance + 5) {
        if (e.deltaY < 0) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };
    const handleKeyDown = (e) => {
      const currentScroll = window.scrollY;
      if (currentScroll < transitionDistance - 5) {
        if (["ArrowDown", "PageDown", " "].includes(e.key)) {
          e.preventDefault();
          window.scrollTo({ top: transitionDistance, behavior: 'smooth' });
        } else if (["ArrowUp", "PageUp"].includes(e.key)) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else if (currentScroll >= transitionDistance - 5 && currentScroll <= transitionDistance + 5) {
        if (["ArrowUp", "PageUp"].includes(e.key)) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isHome, isTablet]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024);

      const baseLogoWidth = 240;
      const horizontalPadding = 32;
      const availableWidth = Math.max(0, window.innerWidth - horizontalPadding * 2);
      setHomeLogoStartScale(Math.min(6.4, Math.max(1, availableWidth / baseLogoWidth)));
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [sidebar, setSideBar] = useState(false);

  let oldScrollY = 0;
  const [isSticky, setIsSticky] = useState(false);

  const [Style, setStyle] = useState({
    background: 'transparent',
    position: 'fixed',
    width: '100%',
    zIndex: 999,
    paddingTop: '15px',
    paddingBottom: '15px',
  });

  const controlDirection = () => {
    if (flag === 3) return;
    if (!isMobile && window.scrollY > 500 && window.scrollY > oldScrollY) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
    oldScrollY = window.scrollY;
  };

  useEffect(() => {
    if (isSticky) {
      setStyle({
        background: 'black',
        position: 'fixed',
        width: '100%',
        zIndex: 999,
        paddingTop: '15px',
        paddingBottom: '15px',
      });
    } else {
      setStyle({
        background: 'transparent',
        position: 'fixed',
        width: '100%',
        zIndex: 999,
        paddingTop: '15px',
        paddingBottom: '15px',
      });
    }
  }, [isSticky]);

  useEffect(() => {
    window.addEventListener('scroll', controlDirection);
    return () => {
      window.removeEventListener('scroll', controlDirection);
    };
  }, []);

  const navBackground = useTransform(scrollY, [0, 1000], ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 1)'], { ease: cubicBezier(0.5, 0, 0, 1) });
  const navContentOpacity = useTransform(scrollY, [0, 100], [0, 1], { ease: cubicBezier(0.5, 0, 0, 1) });
  const pattiHeight = useTransform(scrollY, [0, 200], ['35px', '0px'], { ease: cubicBezier(0.5, 0, 0, 1) });
  const pattiOpacity = useTransform(scrollY, [0, 200], [1, 0], { ease: cubicBezier(0.5, 0, 0, 1) });
  const navPadding = useTransform(scrollY, [0, 200], ['45px', '15px'], { ease: cubicBezier(0.5, 0, 0, 1) });

  const accountClick = () => {
    if (getState()) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  return (
    <motion.nav
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false }}
      transition={{ duration: 0.5 }}
      variants={{ visible: { opacity: 1 }, hidden: { opacity: 0 } }}
      className="NavBar"
      style={
        flag === 3
          ? {
              backgroundColor: navBackground,
              position: 'fixed',
              top: '0px',
              width: '100%',
              zIndex: 999,
              paddingTop: '0px',
              paddingBottom: '0px',
            }
          : flag === 1
          ? {
              position: 'absolute',
              width: '100%',
              zIndex: 999,
              paddingTop: '15px',
              paddingBottom: '15px',
            }
          : flag === 0
          ? Style
          : flag === 2
          ? { position: 'fixed', width: '100%', zIndex: 999, paddingTop: '15px', paddingBottom: '15px' }
          : flag === 4
          ? { position: 'relative', width: '100%', zIndex: 999, paddingTop: '15px', paddingBottom: '15px' }
          : {}
      }
    >
      <div className="nav_wrapper" style={sidebar ? { display: 'block', opacity: '1' } : {}} onClick={() => setSideBar(false)}></div>
      <SidebarNav sidebar={sidebar} setSideBar={setSideBar} />

      {flag === 3 && !isSticky && (
        <motion.div style={{ height: pattiHeight, opacity: pattiOpacity, overflow: 'hidden' }}>
          <Patti_home />
        </motion.div>
      )}
      <motion.div className="Navbody txt-font" style={flag === 3 ? { paddingTop: isMobile ? '0px' : navPadding, paddingBottom: isMobile ? '0px' : navPadding } : {}}>
        {isMobile ? (
          <MobileNavBar sidebar={sidebar} setSideBar={setSideBar} footerLogoOpacity={footerLogoOpacity} />
        ) : (
          <div className="nav-desktop max-w-[98%] xl:max-w-[1600px] mx-auto" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <motion.div className="nav-left txt-font1" style={isHome ? { opacity: navContentOpacity } : {}}>
              <div type="button">
                <img src={burger_icon} style={{ width: '30px' }} type="button" className="sidenav_burger" onClick={() => setSideBar(!sidebar)} />
              </div>
            </motion.div>

            <div style={{ position: 'relative', zIndex: 1000 }}>
              <a href="/" style={{ textDecoration: 'none', display: 'block' }}>
                {isHome ? (
                  <motion.img
                    src={Logo1}
                    alt="Logo"
                    className="logo_main"
                    style={{ transformOrigin: 'center', scale: logoScale, y: logoY }}
                    onClick={() => {}}
                    type="button"
                  />
                ) : (
                  <img
                    src={Logo1}
                    alt="Logo"
                    className="logo_main"
                    style={{ display: 'block', opacity: 1, width: '240px', height: 'auto' }}
                    onClick={() => {}}
                    type="button"
                  />
                )}
              </a>
            </div>

            <motion.div className="nav-right txt-font1" style={isHome ? { opacity: navContentOpacity } : {}}>
              <a href="/contact" style={{ textDecoration: 'none' }}>
                <Phone className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
              <a href="/cart" style={{ textDecoration: 'none' }}>
                <ShoppingBag className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
              <a style={{ textDecoration: 'none', cursor: 'pointer' }} onClick={accountClick}>
                <User className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.nav>
  );
}
